import styled, { css } from 'styled-components';
import Link from 'next/link';
import format from 'date-fns/format';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import TYPO, { H5, P2, weight, fluidType } from 'styles/typography';

import { handleReplaceMoorgate } from 'utils/helpers';

import BgCircleMask from 'public/images/backgrounds/bg-session-mask.svg';

import Avatar from './Avatar';

const CardLink = styled.a`
  margin-bottom: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  min-height: 300px;
  border-radius: 5px;
  background-color: ${COLORS.pinkMuted};
  color: ${COLORS.forest};
  text-decoration: none;
  overflow: hidden;

  ${BREAKPOINTS.small`
    max-width: 370px;
    min-height: 450px;
  `}

  ${BREAKPOINTS.medium`
    max-width: 420px;
    min-height: 550px;
  `}
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 216px;

  ${BREAKPOINTS.small`
    min-height: 220px;
  `}

  ${BREAKPOINTS.medium`
    min-height: 240px;
  `}
`;

const CircleMask = styled.div`
  clip-path: url(#bg-session-mask);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 195px;
  background-color: ${COLORS.white};

  /* Fix clip-path in iOS Safari */
  transform: translateZ(0);

  ${BREAKPOINTS.small`
    height: 240px;
  `};
`;

const SvgClipPath = styled(BgCircleMask)`
  position: absolute;
  top: 0;
  left: 0;
`;

const Location = styled.div`
  padding: 15px 16px;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  z-index: 10;

  ${BREAKPOINTS.small`
    padding: 20px 25px;
  `}
`;

const Subtitle = styled(P2)`
  margin: 0;
  opacity: 1;
`;

const Players = styled.div`
  display: flex;
  align-items: flex-end;
  transform: translateY(35px);

  ${BREAKPOINTS.small`
    transform: translateY(48px);
  `}
`;

const Player = styled(Avatar)`
  margin: 0 4px;

  ${({ twoPlayerLayout }) =>
    twoPlayerLayout
      ? css`
          &:nth-child(1) {
            transform: translateX(20%);
            z-index: 10;
          }
          &:nth-child(2) {
            transform: translateX(-20%);
          }
        `
      : css`
          &:nth-child(2) {
            order: -1;
          }
        `}

  ${BREAKPOINTS.small`
    margin: 0 6px;
  `}
`;

const DateTitle = styled(H5)`
  position: absolute;
  top: 58px;
  width: 100%;
  text-align: center;

  ${BREAKPOINTS.small`
    margin-top: 10px;
    position: static;
  `}

  ${BREAKPOINTS.medium`
    margin-top: 30px;
  `}
`;

const Br = styled.br`
  display: none;

  ${BREAKPOINTS.small`
    display: block;
  `}
`;

const Overview = styled.div`
  margin: 7px auto 0;
  min-width: 290px;
  display: flex;
  justify-content: space-between;

  ${BREAKPOINTS.small`
    margin: auto 20px 20px;
    padding: 22px 5px 16px;
    background-color: ${COLORS.white};
    border-radius: 8px;
  `}

  ${BREAKPOINTS.medium`
    margin: auto 20px 30px;
    padding: 25px 32px;
  `}
`;

const Item = styled.div`
  min-width: 82px;
  text-align: center;
`;

const Number = styled.div`
  font-size: ${fluidType(25, 35)};
  line-height: ${fluidType(20, 30)};
  font-weight: ${weight.medium};
`;

const SmallText = styled.span`
  ${TYPO.small}

  ${BREAKPOINTS.small`
    ${TYPO.p2}
  `}
`;

const SessionCard = ({
  sessionId,
  index,
  locationName,
  positions,
  playerCount,
  gameCount,
  sessionMomentsCount,
  startTimestamp,
  className,
}) => {
  const sessionDate = new Date(startTimestamp || null);
  const dateString = format(sessionDate, 'do');
  const monthString = format(sessionDate, 'MMMM');
  const yearString = format(sessionDate, 'yyyy');

  const cardNo = `-${index < 10 && '0'}${index}`;
  return (
    <Link href={`/my-clays/sessions/${sessionId}`} passHref>
      <CardLink className={className}>
        <Location>
          <Subtitle>{handleReplaceMoorgate(locationName)}</Subtitle>
          <Subtitle>{cardNo}</Subtitle>
        </Location>

        <Header>
          <CircleMask />
          <SvgClipPath />

          <Players>
            {positions.map(({ position, avatar }) => {
              const isSmall = positions.length > 2 && position !== '1';
              return (
                <Player
                  key={position}
                  position={position}
                  imgUrl={avatar}
                  size={isSmall ? 'small' : 'large'}
                  twoPlayerLayout={positions.length === 2}
                />
              );
            })}
          </Players>
        </Header>

        <DateTitle small>
          {monthString} {dateString} <Br /> {yearString}
        </DateTitle>

        <Overview>
          <Item>
            <Number>{playerCount}</Number>
            <SmallText>players</SmallText>
          </Item>
          <Item>
            <Number>{gameCount}</Number>
            <SmallText>games played</SmallText>
          </Item>
          <Item>
            <Number>{sessionMomentsCount}</Number>
            <SmallText>moments</SmallText>
          </Item>
        </Overview>
      </CardLink>
    </Link>
  );
};

export default SessionCard;
